import { useState, useEffect } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

type Profile = {
  name: string;
  email: string;
  department: string;
  phone: string;
  profile_img: string;
  role: string;
};

export default function SettingsPage() {
  const { user } = useAuth();

  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [profile, setProfile] = useState<Profile>({
    name: '',
    email: '',
    department: '',
    phone: '',
    profile_img: '',
    role: ''
  });

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);

        const res = await fetch(
          `https://guardiancloudcare.com/backend/api/get_profile.php?uid=${encodeURIComponent(user.uid)}`
        );

        const data = await res.json();

        if (!data.success) {
          alert(data.error || data.message || 'Unable to load profile.');
          return;
        }

        setProfile({
          name: data.user?.name || '',
          email: data.user?.email || '',
          department: data.user?.department || '',
          phone: data.user?.phone_number || '',
          profile_img: data.user?.profile_img || '',
          role: data.user?.role || ''
        });
      } catch (err) {
        console.error('Profile Fetch Error:', err);
        alert('Unable to connect to the server.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user?.uid]);

  const handleSave = async () => {
    if (!user?.uid) {
      alert('User session not found.');
      return;
    }

    if (!profile.name.trim()) {
      alert('Full Name is required.');
      return;
    }

    if (!profile.email.trim()) {
      alert('Email Address is required.');
      return;
    }

    try {
      setSaving(true);
      setSaved(false);

      const res = await fetch(
        'https://guardiancloudcare.com/backend/api/update_social_worker_profile.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            uid: user.uid,
            name: profile.name.trim(),
            email: profile.email.trim(),
            department: profile.department.trim(),
            phone_number: profile.phone.trim()
          })
        }
      );

      const data = await res.json();

      if (!data.success) {
        alert(data.error || data.message || 'Update failed.');
        return;
      }

      setSaved(true);

      setTimeout(() => {
        setSaved(false);
      }, 2500);
    } catch (err) {
      console.error('Profile Update Error:', err);
      alert('Unable to connect to the server.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!user?.uid) {
      alert('User session not found.');
      return;
    }

    if (!currentPassword) {
      alert('Enter your current password.');
      return;
    }

    if (!newPassword) {
      alert('Enter your new password.');
      return;
    }

    if (newPassword.length < 8) {
      alert('New password must be at least 8 characters.');
      return;
    }

    try {
      setPasswordSaving(true);

      const res = await fetch(
        'https://guardiancloudcare.com/backend/api/change_password.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            uid: user.uid,
            current_password: currentPassword,
            new_password: newPassword
          })
        }
      );

      const data = await res.json();

      if (!data.success) {
        alert(data.error || data.message || 'Password change failed.');
        return;
      }

      setCurrentPassword('');
      setNewPassword('');

      alert('Password changed successfully.');
    } catch (err) {
      console.error('Password Change Error:', err);
      alert('Unable to connect to the server.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleAvatarChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];

    if (!file || !user?.uid) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Profile image must not exceed 5 MB.');
      e.target.value = '';
      return;
    }

    try {
      const formData = new FormData();

      formData.append('profile_img', file);
      formData.append('uid', user.uid);

      const res = await fetch(
        'https://guardiancloudcare.com/backend/api/upload_avatar.php',
        {
          method: 'POST',
          body: formData
        }
      );

      const data = await res.json();

      if (!data.success) {
        alert(data.error || data.message || 'Avatar upload failed.');
        return;
      }

      setProfile((prev) => ({
        ...prev,
        profile_img: data.filename || data.profile_img || ''
      }));

      setSaved(true);

      setTimeout(() => {
        setSaved(false);
      }, 2500);
    } catch (err) {
      console.error('Avatar Upload Error:', err);
      alert('Avatar upload failed.');
    } finally {
      e.target.value = '';
    }
  };

  const profileImage = profile.profile_img
    ? `https://guardiancloudcare.com/backend/uploads/${profile.profile_img}`
    : '';

  return (
    <AppLayout title="Settings" breadcrumb={['Home', 'Settings']}>
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-semibold text-gray-800">
                Profile Settings
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Manage your Social Worker account information.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center">
              <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">
                Loading profile...
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-5 mb-6 pb-6 border-b border-gray-100">
                <div className="w-20 h-20 rounded-2xl overflow-hidden border border-gray-200 flex-shrink-0">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-teal-500 flex items-center justify-center text-white text-2xl font-bold">
                      {profile.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                </div>

                <div>
                  <p className="font-semibold text-gray-800">
                    {profile.name || user?.name || 'User'}
                  </p>

                  <p className="text-sm text-gray-500">
                    {profile.email || user?.email || ''}
                  </p>

                  <input
                    type="file"
                    accept="image/*"
                    id="avatarUpload"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      document.getElementById('avatarUpload')?.click()
                    }
                    className="mt-2 text-xs text-teal-500 cursor-pointer whitespace-nowrap hover:text-teal-700"
                  >
                    Change Avatar
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Full Name
                  </label>

                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        name: e.target.value
                      })
                    }
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Email Address
                  </label>

                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        email: e.target.value
                      })
                    }
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Department
                  </label>

                  <input
                    type="text"
                    value={profile.department}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        department: e.target.value
                      })
                    }
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Phone Number
                  </label>

                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        phone: e.target.value
                      })
                    }
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Role
                  </label>

                  <input
                    value={profile.role.replace(/_/g, ' ')}
                    readOnly
                    className="w-full border border-gray-100 bg-gray-50 rounded-xl px-3 py-2.5 text-sm text-gray-500 capitalize"
                  />
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-800">
                    Change Password
                  </p>

                  <p className="text-xs text-gray-400 mt-1">
                    Update your account password securely.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Current Password
                    </label>

                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) =>
                        setCurrentPassword(e.target.value)
                      }
                      placeholder="Current password"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      New Password
                    </label>

                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) =>
                        setNewPassword(e.target.value)
                      }
                      placeholder="New password"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handlePasswordChange}
                    disabled={passwordSaving}
                    className="bg-gray-800 text-white text-sm px-5 py-2.5 rounded-xl hover:bg-gray-900 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {passwordSaving
                      ? 'Changing Password...'
                      : 'Change Password'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-3">
          {saved && (
            <span className="text-sm text-teal-600 flex items-center gap-1 bg-teal-50 border border-teal-200 px-3 py-1.5 rounded-lg">
              <i className="ri-checkbox-circle-line" />
              Settings saved!
            </span>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-teal-500 text-white text-sm px-6 py-2.5 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </AppLayout>
  );
}